// src/pages/auth/tutor/Login.jsx
import React, { useState } from 'react';
import { 
  Box, Typography, TextField, Button, Link, Grid, InputAdornment,
  IconButton, CircularProgress, Alert, Checkbox, FormControlLabel,
  Container, Paper, Avatar, Stack
} from '@mui/material';

import { 
  Lock as LockIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  School as TutorIcon
} from '@mui/icons-material';

import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTutorAuth } from '../../../context/tutorAuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Login() {

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { setTutor } = useTutorAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    try {

      const response = await axios.post(
        `${BASE_URL}/api/auth/tutors/login`,
        { email, password },
        { withCredentials: true }
      );

      const { tutor, token } = response.data;

      console.log('✅ Login success:', tutor);

      localStorage.setItem('tutor', JSON.stringify(tutor));
      localStorage.setItem('tutorToken', token);

      setTutor(tutor);

      navigate('/tutor/dashboard', { replace: true });

      setTimeout(() => {
        if (!window.location.pathname.includes('/tutor/dashboard')) {
          window.location.href = '/tutor/dashboard';
        }
      }, 800);

    } catch (err) {

      console.error('❌ Login error:', err);

      const message =
        err.response?.data?.message || 'Login failed. Please try again.';

      setError(message);

    } finally {

      setLoading(false);

    }
  };

  return (

    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg,#f5f7fa,#c3cfe2)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >

      <Container maxWidth="lg">

        <Paper
          elevation={24}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            borderRadius: '24px',
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >

          {/* LEFT IMAGE PANEL */}

          <Box
            sx={{
              flex: 1.2,
              position: 'relative',
              display: { xs: 'none', md: 'block' }
            }}
          >

            <img
              src="/IMG_8755.jpeg"
              alt="Tutor Login"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />

            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to bottom,transparent,rgba(0,0,0,0.7))',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                p: 4
              }}
            >

              <Typography
                variant="h3"
                sx={{ color: '#fff', fontWeight: 800 }}
              >
                Peer2Learn
              </Typography>

              <Typography sx={{ color: '#eee' }}>
                Empower students by sharing your knowledge
              </Typography>

            </Box>

          </Box>


          {/* RIGHT LOGIN FORM */}

          <Box
            sx={{
              flex: 1,
              p: { xs: 4, md: 6 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >

            <Avatar sx={{ mb: 2, bgcolor: 'secondary.main' }}>
              <TutorIcon />
            </Avatar>

            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Tutor Login
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to manage your tutoring dashboard
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px'
                  }
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px'
                  }
                }}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 1 }}
              >

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                  }
                  label="Remember me"
                />

                <Link href="/tutor/forgot-password" variant="body2">
                  Forgot password?
                </Link>

              </Stack>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.6,
                  borderRadius: '14px',
                  fontWeight: 700,
                  background:
                    'linear-gradient(90deg,#3a86ff,#4361ee)',
                  boxShadow: '0 10px 20px rgba(58,134,255,0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >

                {loading
                  ? <CircularProgress size={24} />
                  : 'Sign In'}

              </Button>

              <Grid container justifyContent="center">

                <Grid>

                  <Link href="/tutor/register" variant="body2">
                    Don't have an account? Register
                  </Link>

                </Grid>

              </Grid>

            </Box>

          </Box>

        </Paper>

      </Container>

    </Box>
  );
}